import { useCallback, useEffect, useRef, useState } from 'react';
import ConfirmDialog from '@/components/ConfirmDialog';
import { PAID_GEN_LEAVE_TITLE, paidGenLeaveMessage } from '@/lib/paidGeneration';

export type AskConfirmFn = (
  title: string,
  message: string,
  opts?: { confirmLabel?: string; cancelLabel?: string },
) => Promise<boolean>;

/** 手机友善二次确认（取代 window.confirm） */
export function useAskConfirm() {
  const [req, setReq] = useState<{
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    resolve: (ok: boolean) => void;
  } | null>(null);

  const askConfirm: AskConfirmFn = useCallback((title, message, opts) => (
    new Promise<boolean>((resolve) => {
      setReq({
        title,
        message,
        confirmLabel: opts?.confirmLabel,
        cancelLabel: opts?.cancelLabel,
        resolve,
      });
    })
  ), []);

  const dialog = (
    <ConfirmDialog
      open={!!req}
      title={req?.title ?? ''}
      message={req?.message ?? ''}
      confirmLabel={req?.confirmLabel}
      cancelLabel={req?.cancelLabel}
      onConfirm={() => { req?.resolve(true); setReq(null); }}
      onCancel={() => { req?.resolve(false); setReq(null); }}
    />
  );

  return { askConfirm, confirmDialog: dialog };
}

type GuardOptions = {
  /** 点数已扣、请求已送出，仍在等待结果 */
  isGenerating: boolean;
  /** 已出结果但用户尚未确认入库（pending_review / 换装预览等） */
  hasPendingResult?: boolean;
  /** 用于提示文案 */
  label?: string;
  askConfirm: AskConfirmFn;
};

/**
 * 耗点生成进行中或待确认结果时：
 * - 关闭/刷新页 → 浏览器提示
 * - 站内跳转 → 弹窗二次确认
 */
export function usePaidGenerationGuard({
  isGenerating,
  hasPendingResult = false,
  label = '本次生成',
  askConfirm,
}: GuardOptions) {
  const shouldBlock = isGenerating || hasPendingResult;
  const askRef = useRef(askConfirm);
  askRef.current = askConfirm;

  useEffect(() => {
    if (!shouldBlock) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [shouldBlock]);

  useEffect(() => {
    if (!shouldBlock) return;
    const onClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement | null)?.closest('a[href]');
      if (!anchor || anchor.getAttribute('target') === '_blank') return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || /^https?:/i.test(href) || !href.startsWith('/')) return;

      e.preventDefault();
      e.stopPropagation();
      void (async () => {
        const ok = await askRef.current(
          PAID_GEN_LEAVE_TITLE,
          paidGenLeaveMessage(label, isGenerating),
          { confirmLabel: '确定离开', cancelLabel: '继续等待' },
        );
        if (ok) window.location.assign(href);
      })();
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [shouldBlock, isGenerating, label]);

  return { shouldBlock };
}
